import { Grid, Paper } from '@mui/material';
import { useContext } from 'react';
import { ConfigContext } from '../../../../Contexts/ConfigContext';
import DropDownSingle from '../../../../Components/DropDownSingle';
import SwitchInput from '../../../../Components/SwitchInput';
import InputConfig from '../../../../Components/InputConfig';
import PageHeading from '../../../../Components/PageHeading';
// constants
import FeaturesDataConstants from './constants.json';
import CodeEditor from '../../../../Components/CodeEditor';
import useCommonStyles from '../../../../commonStyles';

// Initializing Constants

const { FALLBACK_TAG_CONFIG } = FeaturesDataConstants;

const { AD_REQUESTS_THRESHOLD, DELAY_IN_MS, TagContentTypes } = FALLBACK_TAG_CONFIG;

const { SCRIPT, URL, HTML } = TagContentTypes;

const languageMap = {
	[SCRIPT]: 'javascript',
	[HTML]: 'html',
	[URL]: 'URL'
};

/**
 * @name  FallbackTagConfig
 * @description  This component allows users to edit fallback tag settings.
 *
 *  Config Added
 *
 * fallbackTagConfig = {
 *   enabled: boolean;
 *   fallbackTagDetails: {
 *     tagContent: string;
 *     async: boolean;
 *     defer: boolean;
 *   };
 *   fallbackTagType: string;
 *   adRequestsThreshold: number;
 *   delayInMs: number;
 * };
 *
 * @param {Object} keyData - Identifies the config data in rawConfig.
 */
const FallbackTagConfig = ({ keyData, featureDescription }) => {
	const commonStyleClasses = useCommonStyles();
	const { rawConfig, updateRawConfig } = useContext(ConfigContext);
	const fallbackTagTypeKey = `${keyData}.fallbackTagType`;
	const fallbackDetailsKey = `${keyData}.fallbackTagDetails`;
	const adRequestThresholdKey = `${keyData}.adRequestsThreshold`;
	const delayInMsKey = `${keyData}.delayInMs`;
	const enabledKey = `${keyData}.enabled`;
	const selectedTagType = rawConfig[fallbackTagTypeKey];
	const language = languageMap[selectedTagType] || null;

	/**
	 * Sets the selected parameter based on `adRequestsThreshold` or `delayInMs` in `rawConfig`. Defaults to `null` if neither is found.
	 * @type {string|null} selectedParameter - `AD_REQUESTS_THRESHOLD`, `DELAY_IN_MS`, or `null`.
	 */
	let selectedParameter = null;
	if (rawConfig[`${keyData}.adRequestsThreshold`] !== undefined) {
		selectedParameter = AD_REQUESTS_THRESHOLD;
	} else if (rawConfig[`${keyData}.delayInMs`] !== undefined) {
		selectedParameter = DELAY_IN_MS;
	}

	/**
	 * Updates the `tagContent` value in the `fallbackTagConfig` for the fallback tag details.
	 *
	 * @param {string} val - The new value to set for `tagContent`.
	 */
	const updateTagContent = (val) => {
		const clonedConfig = structuredClone(rawConfig);
		clonedConfig[`${fallbackDetailsKey}.tagContent`] = val;
		updateRawConfig(clonedConfig);
	};

	/**
	 * Updates the config based on the selected dropdown value
	 * and remove the previous selected property from the config.
	 *
	 * @param {string} value - The selected value from the dropdown (DELAY_IN_MS or AD_REQUESTS_THRESHOLD).
	 */
	const updateDropdownValueAndConfig = (value) => {
		const clonedConfig = structuredClone(rawConfig);
		const keyToDelete =
			value === DELAY_IN_MS ? `${keyData}.adRequestsThreshold` : `${keyData}.delayInMs`;

		switch (value) {
			case DELAY_IN_MS: {
				clonedConfig[`${keyData}.delayInMs`] = 0;
				delete clonedConfig[keyToDelete];
				break;
			}

			case AD_REQUESTS_THRESHOLD: {
				clonedConfig[`${keyData}.adRequestsThreshold`] = 0;
				delete clonedConfig[keyToDelete];
				break;
			}

			default: {
				break;
			}
		}
		updateRawConfig(clonedConfig);
	};

	return (
		<Paper className={commonStyleClasses.mainPagePaperMarginStyle}>
			<PageHeading
				headingName="Fallback Tag"
				headingPath={['Dashboard', 'Framerate', 'Features Data', 'Fallback Tag']}
				description="Manage Fallback Tag Configuration"
				showInfoButton={true}
				infoMessage={featureDescription}
			/>

			<Grid
				container
				rowSpacing={1}
				columnSpacing={1}
				style={{ margin: '3rem 0 2rem 5rem', width: '96%' }}
			>
				<Grid item xs={2}>
					<SwitchInput
						keyData={enabledKey}
						label={'Enabled'}
						disabled={false}
						defaultChecked={false}
						containerStyles={{ marginTop: '6rem' }}
						object={rawConfig}
						updateObject={updateRawConfig}
					/>
				</Grid>

				<Grid item xs={3}>
					<DropDownSingle
						disabled={!rawConfig[enabledKey]}
						label={'Select Parameter'}
						keyData={'FALLBACK_TAG_CONFIG_PARAMETERS'}
						isObjectEditor={false}
						list={[AD_REQUESTS_THRESHOLD, DELAY_IN_MS]}
						formControlVariant="outlined"
						selectInputStyles={{ marginTop: '.5rem' }}
						directValue={selectedParameter}
						updateDirectValue={(value) => updateDropdownValueAndConfig(value)}
					/>
				</Grid>

				<Grid item xs={3}>
					<DropDownSingle
						disabled={!rawConfig[enabledKey]}
						keyData={fallbackTagTypeKey}
						label={'FallbackTag Type'}
						selectInputStyles={{ marginTop: '.5rem' }}
						list={[SCRIPT, URL, HTML]}
						object={rawConfig}
						updateObject={updateRawConfig}
					/>
				</Grid>

				<RenderSelectedParameters
					rawConfig={rawConfig}
					updateRawConfig={updateRawConfig}
					keyData={keyData}
					enabledKey={enabledKey}
					adRequestThresholdKey={adRequestThresholdKey}
					delayInMsKey={delayInMsKey}
				/>

				{selectedTagType !== HTML && (
					<RenderSwitchInputs
						rawConfig={rawConfig}
						updateRawConfig={updateRawConfig}
						fallbackDetailsKey={fallbackDetailsKey}
						enabledKey={enabledKey}
					/>
				)}

				<RenderCodeEditorAndInput
					rawConfig={rawConfig}
					updateRawConfig={updateRawConfig}
					updateTagContent={updateTagContent}
					fallbackDetailsKey={fallbackDetailsKey}
					enabledKey={enabledKey}
					language={language}
					selectedTagType={selectedTagType}
				/>
			</Grid>
		</Paper>
	);
};

const RenderSelectedParameters = ({
	rawConfig,
	updateRawConfig,
	adRequestThresholdKey,
	delayInMsKey,
	enabledKey
}) => {
	return (
		<>
			{rawConfig[adRequestThresholdKey] !== undefined && (
				<Grid item xs={3}>
					<InputConfig
						editable={!!rawConfig[enabledKey]}
						keyData={adRequestThresholdKey}
						label={'Ad Requests Threshold'}
						inputType="number"
						object={rawConfig}
						updateObject={updateRawConfig}
					/>
				</Grid>
			)}

			{rawConfig[delayInMsKey] !== undefined && (
				<Grid item xs={3}>
					<InputConfig
						editable={!!rawConfig[enabledKey]}
						keyData={delayInMsKey}
						label={'Delay in ms'}
						inputType="number"
						object={rawConfig}
						updateObject={updateRawConfig}
					/>
				</Grid>
			)}
		</>
	);
};

const RenderSwitchInputs = ({ rawConfig, updateRawConfig, fallbackDetailsKey, enabledKey }) => (
	<>
		<Grid item xs={2.4}>
			<SwitchInput
				disabled={!rawConfig[enabledKey]}
				keyData={`${fallbackDetailsKey}.defer`}
				label={'Defer'}
				object={rawConfig}
				updateObject={updateRawConfig}
				containerStyles={{ marginTop: '4.5rem' }}
			/>
		</Grid>
		<Grid item xs={2.5}>
			<SwitchInput
				disabled={!rawConfig[enabledKey]}
				keyData={`${fallbackDetailsKey}.async`}
				label={'Async'}
				object={rawConfig}
				updateObject={updateRawConfig}
				containerStyles={{ marginTop: '4.5rem' }}
			/>
		</Grid>
	</>
);

const RenderCodeEditorAndInput = ({
	rawConfig,
	updateRawConfig,
	updateTagContent,
	fallbackDetailsKey,
	enabledKey,
	language,
	selectedTagType
}) => {
	const tagContentKey = `${fallbackDetailsKey}.tagContent`;

	return (
		<>
			{selectedTagType !== URL && (
				<Grid item xs={12}>
					{selectedTagType === HTML && (
						<CodeEditor
							height="40rem"
							language={language}
							value={rawConfig[tagContentKey]}
							editorOptions={{ readOnly: !rawConfig[enabledKey] }}
							updateCode={updateTagContent}
							containerStyles={{ width: '100%', margin: '3rem auto' }}
						/>
					)}

					{selectedTagType === SCRIPT && (
						<CodeEditor
							height="40rem"
							language={language}
							value={rawConfig[tagContentKey]}
							editorOptions={{ readOnly: !rawConfig[enabledKey] }}
							updateCode={updateTagContent}
							containerStyles={{ width: '100%', margin: '3rem auto' }}
						/>
					)}
				</Grid>
			)}
			{selectedTagType === URL && (
				<InputConfig
					editable={!!rawConfig[enabledKey]}
					keyData={tagContentKey}
					label="Url Content"
					object={rawConfig}
					updateObject={updateRawConfig}
					labelStyles={{ marginTop: '1rem' }}
					containerStyles={{
						paddingTop: '4rem'
					}}
				/>
			)}
		</>
	);
};

export default FallbackTagConfig;
